import { computed, onBeforeUnmount, ref, watch } from 'vue'
import type { ChatStatus } from 'ai'
import type { PendingWakeup, UIChatMessage } from 'models'
import type { ChatbotApiClient } from 'composables'

/** How long after `dueAt` the first look comes: the turn takes a few seconds. */
const FIRST_LOOK_MS = 4000
const BACKOFF_MS = [3000, 5000, 10_000]
/** A wakeup whose turn never shows up (skipped, failed) stops being watched. */
const WATCH_MAX_MS = 120_000
/** The server forgets the presence after two lost signals (75 seconds). */
const PRESENCE_INTERVAL_MS = 30_000

/**
 * Keeps an open chat in step with its wakeups (spec agent-wakeup, "Consegna"):
 * nothing pushes to the page, so it reads the pending wakeup, sleeps until its
 * time and then reloads the conversation until the wakeup's turn is in it,
 * which the seed carrying its id proves (the seed and the answer are written
 * together). Then it reads the next one, if the turn scheduled it. The
 * pending one is read when the chat changes and after every turn of the
 * user, typed or spoken, which may have scheduled it.
 *
 * While a wakeup waits, the page also tells the server it is in front of the
 * user (Fase 1.2): every 30 seconds while the tab is visible, and at once
 * when it comes back in front, so a round with news mails only a user who
 * is away. A page with no wakeup waiting sends nothing.
 */
export function useWakeupWatch(options: {
    agentId: () => string
    chatId: () => string
    apiClient: () => ChatbotApiClient
    messages: () => UIChatMessage[]
    refreshMessages: () => Promise<unknown>
    status: () => ChatStatus
}) {
    const pendingWakeup = ref<PendingWakeup | null>(null)
    const isStopping = ref(false)
    let timer: ReturnType<typeof setTimeout> | undefined
    let isUnmounted = false

    const clear = () => {
        clearTimeout(timer)
        timer = undefined
    }

    const later = (delayMs: number, run: () => Promise<void>) => {
        clear()
        if (!isUnmounted) {
            timer = setTimeout(() => void run(), delayMs)
        }
    }

    const hasTurnOf = (wakeId: string) =>
        options
            .messages()
            .some((message) => message.metadata?.wakeup?.id === wakeId)

    /** Reads the pending wakeup and arms the first look at its time. */
    async function check(): Promise<void> {
        clear()
        const chatId = options.chatId()
        try {
            const state = await options
                .apiClient()
                .getChatWakeup(options.agentId(), chatId)
            if (chatId !== options.chatId()) {
                return
            }
            pendingWakeup.value = state.pendingWakeup
        } catch {
            // A chat not written yet, or a request that failed: nothing to
            // watch until the next turn asks again.
            pendingWakeup.value = null
        }
        const pending = pendingWakeup.value
        if (pending) {
            const wait = Date.parse(pending.dueAt) - Date.now() + FIRST_LOOK_MS
            later(Math.max(FIRST_LOOK_MS, wait), () =>
                watchTurn(pending.wakeId, Date.now(), 0),
            )
        }
    }

    async function watchTurn(wakeId: string, since: number, step: number) {
        await options.refreshMessages()
        if (isUnmounted) {
            return
        }
        if (hasTurnOf(wakeId) || Date.now() - since >= WATCH_MAX_MS) {
            await check()
            return
        }
        // Still not there: maybe deferred behind a turn of the user, whose
        // new time `check` would read; keep looking until the cap.
        later(BACKOFF_MS[Math.min(step, BACKOFF_MS.length - 1)], () =>
            watchTurn(wakeId, since, step + 1),
        )
    }

    /** The user's "Stop": the pending wakeup never runs. */
    async function stop(): Promise<void> {
        isStopping.value = true
        try {
            const state = await options
                .apiClient()
                .stopChatWakeup(options.agentId(), options.chatId())
            clear()
            pendingWakeup.value = state.pendingWakeup
        } finally {
            isStopping.value = false
        }
    }

    /**
     * Another chat on screen: the watch of the previous one ends, or its
     * timer would go on reloading the new chat for minutes.
     */
    function reset(): void {
        clear()
        pendingWakeup.value = null
    }

    // #region spoken turns
    /**
     * The chat of a spoken question whose turn is not written yet. A call
     * leaves the chat `ready`, so the status does not show its turns: the
     * page says when one starts and when the reloaded chat has them all.
     */
    let spokenTurnUnwrittenIn: string | undefined

    /** A spoken question entered the chat: the server answers its turn. */
    function spokenTurnStarted(): void {
        spokenTurnUnwrittenIn = options.chatId()
    }

    /**
     * The reloaded chat has every spoken question written: their turns may
     * have scheduled a wakeup.
     */
    function spokenTurnsWritten(): void {
        if (spokenTurnUnwrittenIn === options.chatId()) {
            spokenTurnUnwrittenIn = undefined
            void check()
        }
    }

    /** The page stopped waiting: a turn still unwritten is not coming. */
    function forgetSpokenTurns(): void {
        spokenTurnUnwrittenIn = undefined
    }
    // #endregion spoken turns

    // #region when to read
    const hasQuestions = computed(() =>
        options.messages().some((message) => message.role === 'user'),
    )
    // A chat with no question has none, and the messages of a chat opened
    // later arrive after its id: the read waits for its questions. A question
    // just sent is read after its turn instead, by the watch below or, for a
    // spoken one, by `spokenTurnsWritten`: the first one of a new chat has no
    // row on the server yet, and the read got a 404.
    watch(
        [options.chatId, hasQuestions],
        ([, questions]) => {
            reset()
            const status = options.status()
            const answering =
                status === 'submitted' ||
                status === 'streaming' ||
                spokenTurnUnwrittenIn === options.chatId()
            if (questions && !answering) {
                void check()
            }
        },
        { immediate: true },
    )
    watch(options.status, (status, previous) => {
        if (status === 'ready' && previous !== 'ready' && hasQuestions.value) {
            void check()
        }
    })
    // #endregion when to read

    // #region presence
    let presenceTimer: ReturnType<typeof setInterval> | undefined

    /** Best effort: a lost signal costs at most one mail the user did not need. */
    function sendPresence(): void {
        if (!pendingWakeup.value || document.visibilityState !== 'visible') {
            return
        }
        options
            .apiClient()
            .markChatWakeupPresence(options.agentId(), options.chatId())
            .catch(() => undefined)
    }

    const onVisibilityChange = () => sendPresence()
    document.addEventListener('visibilitychange', onVisibilityChange)

    watch(
        () => pendingWakeup.value?.wakeId,
        (wakeId) => {
            clearInterval(presenceTimer)
            presenceTimer = undefined
            if (wakeId && !isUnmounted) {
                sendPresence()
                presenceTimer = setInterval(sendPresence, PRESENCE_INTERVAL_MS)
            }
        },
    )
    // #endregion presence

    onBeforeUnmount(() => {
        isUnmounted = true
        clear()
        clearInterval(presenceTimer)
        document.removeEventListener('visibilitychange', onVisibilityChange)
    })

    return {
        pendingWakeup,
        isStopping,
        check,
        reset,
        stop,
        spokenTurnStarted,
        spokenTurnsWritten,
        forgetSpokenTurns,
    }
}
